import { customElement } from '../../core/ids-decorators';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import { escapeHTML } from '../../utils/ids-xss-utils/ids-xss-utils';

import type IdsDataGrid from './ids-data-grid';
import type IdsIcon from '../ids-icon/ids-icon';
import { IdsDataGridColumn, IdsDataGridColumnGroup } from './ids-data-grid-column';
import IdsMenuButton from '../ids-menu-button/ids-menu-button';

@customElement('ids-data-grid-header')
export default class IdsDataGridHeader extends IdsEventsMixin(IdsElement) {
  rootNode?: any;

  headerCheckbox?: HTMLElement;

  columnCheckbox?: NodeListOf<HTMLInputElement>;

  /**
   * Tracks the state of the header expander
   */
  isHeaderExpanderCollapsed = false;

  constructor() {
    super({ noShadowRoot: true });
    this.state = {
      headerMenuData: null
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.#attachEventHandlers();
  }

  /**
   * Reference to the data grid parent
   * @returns {IdsDataGrid} the data grid parent
   */
  get dataGrid() {
    if (!this.rootNode) this.rootNode = (this.getRootNode() as any).host;
    return (this.rootNode) as IdsDataGrid;
  }

  /* Returns all the ids-icon.header-expander elements in an array */
  get expanderIcons(): IdsIcon[] {
    return [...this.querySelectorAll<IdsIcon>('ids-icon.header-expander')];
  }

  /** @see IdsDataGridHeader.columns */
  #columns?: HTMLElement[];

  /* Returns all the columns in the header */
  get columns(): HTMLElement[] {
    if (!this.#columns?.length) {
      this.#columns = [...this.querySelectorAll<HTMLElement>('.ids-data-grid-header-cell')];
    }
    return this.#columns;
  }

  /**
   * Returns all the columns in the header
   * @see IdsDataGridCell.isOnScreen()
   * @returns {HTMLElement[]} array of onscreen header columns
   */
  get columnsOnscreen(): HTMLElement[] {
    return [...this.querySelectorAll<HTMLElement>('.ids-data-grid-header-cell[column-onscreen]')];
  }

  /**
   * Handle all header related events
   * @private
   */
  #attachEventHandlers() {
    this.#attachSelectionHandler();
    this.#attachSortHandler();
    this.#attachResizeHandlers();
    this.#attachReorderHandlers();
    this.#attachFilterBtnHandler();
  }

  /**
   * Attach handlers to sort via clicking the headers
   * @private
   */
  #attachSortHandler() {
    this.offEvent('click.sort', this);
    this.onEvent('click.sort', this, (e: any) => {
      // Dont sort on resize
      if (this.dataGrid?.isResizing) {
        this.dataGrid.isResizing = false;
        return;
      }

      if (this.dataGrid?.isSelecting) {
        this.dataGrid.isSelecting = false;
        return;
      }

      // Expander collapse/expand all expandable or tree rows
      if (e.target?.classList?.contains('header-expander')) {
        const isColumnHeaderExpander = e.target?.closest('.column-header-expander');
        if (!isColumnHeaderExpander && !this.dataGrid?.showHeaderExpander) return;

        const wasHeaderExpanderCollapsed = this.isHeaderExpanderCollapsed;
        this.isHeaderExpanderCollapsed = !wasHeaderExpanderCollapsed;

        if (wasHeaderExpanderCollapsed) {
          this.dataGrid.expandAll();
        } else {
          this.dataGrid.collapseAll();
        }
        return;
      }

      const sortableHeaderCell = e.target.closest('.is-sortable')?.closest('.ids-data-grid-header-cell');
      if (!sortableHeaderCell) return;
      const id = sortableHeaderCell.getAttribute('column-id');
      const state = sortableHeaderCell.getAttribute('aria-sort') !== 'ascending';

      if (!this.dataGrid.disableClientSort) {
        this.dataGrid?.setSortColumn(id, state);
      } else {
        this.dataGrid?.setSortState(id, state, true);
      }

      this.dataGrid?.updateSelectionBorder(true);
      this.dataGrid?.updateSelectedCells();
    });
  }

  /**
   * Attach handlers to sort via clicking the headers
   * @private
   */
  #attachSelectionHandler() {
    // Select all/deselect all
    this.headerCheckbox = <HTMLElement> this.querySelector('ids-data-grid-header .ids-data-grid-checkbox-container .ids-data-grid-checkbox');
    this.offEvent('click.select', this.headerCheckbox);
    this.onEvent('click.select', this.headerCheckbox, (e: any) => {
      if (e.target.classList.contains('checked') || e.target.classList.contains('indeterminate')) {
        this.dataGrid?.deSelectAllRows();
      } else {
        this.dataGrid?.selectAllRows();
      }
    });

    // Select column
    this.columnCheckbox = this.querySelectorAll<HTMLInputElement>('ids-data-grid-header .ids-data-grid-header-text .ids-checkbox input');
    for (let i = 0; i < this.columnCheckbox.length; i++) {
      this.offEvent('click', this.columnCheckbox[i]);
      this.onEvent('click', this.columnCheckbox[i], () => {
        const isSelect = this.columnCheckbox![i].checked;
        this.dataGrid.isSelecting = true;

        if (isSelect) {
          this.dataGrid?.selectColumn(i);
        } else {
          this.dataGrid?.deSelectColumn(i);
        }
      });
    }
  }

  /**
   * Establish Drag handlers for resize
   * Based on https://htmldom.dev/resize-columns-of-a-table/
   * @private
   */
  #attachResizeHandlers() {
    // Track the current position of mouse
    let x = 0;
    let w = 0;
    let columnId = '';
    let columnElem: HTMLElement;

    const mouseMoveHandler = (e: MouseEvent) => {
      e.preventDefault();
      // Determine how far the mouse has been moved
      const dx = e.clientX - x;

      // Check if this is the last column
      const isLastColumn = this.dataGrid?.columns[this.dataGrid.columns.length - 1]?.id === columnId;

      if (isLastColumn) {
        const headerElement = this.closest('.ids-data-grid-header');
        const parentElement = headerElement?.parentElement;

        if (headerElement && parentElement) {
          const newWidth = w + (!this.dataGrid?.localeAPI.isRTL() ? dx : -dx);

          // Only allow resize if the total width won't become smaller than the col general min width
          if (newWidth >= 92) {
            this.dataGrid?.setColumnWidth(columnId, newWidth);
          }
        }
      } else {
        // Not the last column, proceed with normal resize
        this.dataGrid?.setColumnWidth(columnId, w + (!this.dataGrid?.localeAPI.isRTL() ? dx : -dx));
      }

      this.dataGrid?.updateSelectionBorder();
    };

    // When user releases the mouse, remove the existing event listeners
    const mouseUpHandler = () => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);

      this?.style.setProperty('cursor', '');
      requestAnimationFrame(() => {
        this.dataGrid.isResizing = false;
        columnElem?.parentElement?.querySelectorAll('[draggable]').forEach((el: Element) => el.setAttribute('draggable', 'true'));
      });
    };

    // Add a resize Handler
    this.offEvent('mousedown.resize', this);
    this.onEvent('mousedown.resize', this, (e: MouseEvent) => {
      const target = (e.target as any);
      if (!target.classList.contains('resizer')) {
        return;
      }

      // Get the current mouse position
      x = e.clientX;

      // Calculate the current width of column
      columnElem = target.closest('.ids-data-grid-header-cell') as HTMLElement;
      columnElem.parentElement?.querySelectorAll('[draggable]').forEach((el) => el.setAttribute('draggable', 'false'));

      const colStyles = window.getComputedStyle(columnElem);
      columnId = columnElem.getAttribute('column-id') || '';
      w = parseInt(colStyles.width, 10);

      // Attach listeners for document's events
      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);

      // Import the cursor behavior
      this?.style.setProperty('cursor', 'col-resize');

      // Prevent a click causing a sort
      this.dataGrid.isResizing = true;
    });
  }

  /**
   * Establish Reorder handlers for moving columns
   * @private
   */
  #attachReorderHandlers() {
    const dragArrows = this.dataGrid?.wrapper?.querySelector<HTMLElement>('.ids-data-grid-sort-arrows');
    let dragger: HTMLElement;
    let startIndex = 0;
    let dragInitiated = false;
    let dragParent: HTMLElement | null;

    this.offEvent('mousedown.reorder', this);
    this.onEvent('mousedown.reorder', this, (e: MouseEvent) => {
      const nodeName = (e.target as HTMLElement).nodeName;

      if (nodeName === 'IDS-INPUT') {
        dragParent = (e.target as HTMLElement).closest('.ids-data-grid-header-cell');
        dragParent?.setAttribute('draggable', 'false');
      }
    });

    this.offEvent('mouseup.reorder', this);
    this.onEvent('mouseup.reorder', this, () => {
      dragParent?.setAttribute('draggable', 'true');
    });

    // Style the Dragger
    this.offEvent('dragstart.reorder', this);
    this.onEvent('dragstart.reorder', this, (e: DragEvent) => {
      // Check if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      const target = (e.target as any);
      const nodeName = (e.target as HTMLElement).nodeName;
      if (this.dataGrid.isResizing || !target || nodeName === 'IDS-INPUT') return;
      dragInitiated = true;
      target.classList.add('active-drag-column');
      dragger = target.cloneNode(true);
      dragger.classList.add('dragging');

      if (this.dataGrid?.localeAPI.isRTL()) {
        dragger.style.insetInlineStart = `${this.offsetLeft}px`;
        dragger.style.zIndex = '-5';
      }

      this?.appendChild(dragger);
      // Based on width of 110
      e?.dataTransfer?.setDragImage(dragger, this.dataGrid?.localeAPI.isRTL() ? 100 : 10, 18);
      startIndex = target.getAttribute('aria-colindex');
    });

    // Show the arrows
    this.offEvent('dragenter.reorder', this);
    this.onEvent('dragenter.reorder', this, (e: DragEvent) => {
      if (!dragInitiated) {
        // Accept Dropped Text
        if ((e.target as HTMLElement)?.getAttribute('color-variant') === 'alternate-formatter') {
          (e.target as any).style.backgroundColor = 'var(--ids-data-grid-filter-input-drop-background-color)';
        }
        return;
      }
      const cell = (e.target as any).closest('.ids-data-grid-header-cell');
      if (!cell) return;
      if (cell.classList.contains('active-drag-column')) {
        dragArrows?.style.setProperty('display', 'none');
        return;
      }

      const rect = cell.getBoundingClientRect();
      const curIndex = parseInt(cell.getAttribute('aria-colindex') || '0', 10);
      const startIdx = parseInt(startIndex.toString() ?? '0', 10);

      let cellLeft = rect.left + (startIdx < curIndex ? rect.width + 1 : 1);
      const cellRight = rect.left + (startIdx < curIndex ? 1 : rect.width + 1);
      let offsetLeft = 0;
      if (this.offsetParent) {
        offsetLeft = this.offsetParent.getBoundingClientRect().left
          - (this.offsetParent as HTMLElement).offsetLeft;
      }
      cellLeft = cellLeft < 3 ? 0 : cellLeft;
      dragArrows?.style.setProperty('display', '');
      dragArrows?.style.setProperty('inset-inline-start', `${this.dataGrid?.localeAPI.isRTL() ? offsetLeft - cellRight : cellLeft - offsetLeft}px`);
      dragArrows?.style.setProperty('height', `${rect.height - 2}px`);
      dragArrows?.style.setProperty('display', 'block');

      e.preventDefault();
    });

    // Use a normal cursor (not drag and drop)
    this.offEvent('dragover.reorder', this);
    this.onEvent('dragover.reorder', this, (e: DragEvent) => {
      if (!dragInitiated) return;
      e.dataTransfer!.dropEffect = 'move';
      e.preventDefault();
    });

    this.offEvent('dragleave.reorder', this);
    this.onEvent('dragleave.reorder', this, (e: DragEvent) => {
      if (!dragInitiated && (e.target as HTMLElement)?.getAttribute('color-variant') === 'alternate-formatter') {
        (e.target as any).style.backgroundColor = '';
      }
    });

    const removeDragger = (e: DragEvent) => {
      this.querySelector('.active-drag-column')?.classList.remove('active-drag-column');
      dragger?.remove();
      dragArrows?.style.setProperty('display', 'none');
      e.preventDefault();
    };

    // Set everything temp element back to normal
    this.offEvent('dragend.reorder', this);
    this.onEvent('dragend.reorder', this, (e: DragEvent) => {
      if (!dragInitiated) {
        if ((e.target as HTMLElement)?.getAttribute('color-variant') === 'alternate-formatter') {
          (e.target as any).style.backgroundColor = '';
        }
        return;
      }
      dragInitiated = false;
      removeDragger(e);
    });

    this.offEvent('drop.reorder', this);
    this.onEvent('drop.reorder', this, (e: DragEvent) => {
      if (!dragInitiated) {
        if ((e.target as HTMLElement)?.getAttribute('color-variant') === 'alternate-formatter') {
          (e.target as any).style.backgroundColor = '';
        }
        return;
      }
      dragInitiated = false;
      const cell = (e.target as any).closest('.ids-data-grid-header-cell');
      if (!cell) return;
      this.dataGrid?.moveColumn(startIndex - 1, cell.getAttribute('aria-colindex') - 1);
      removeDragger(e);
    });
  }

  /**
   * Attaches the event handler for the filter button click event.
   */
  #attachFilterBtnHandler() {
    this.offEvent('click.filterbtn', this);
    this.onEvent('click.filterbtn', this, (evt: CustomEvent) => {
      const menuButton = evt.target;
      if (menuButton instanceof IdsMenuButton && menuButton.nextElementSibling?.nodeName !== 'IDS-POPUP-MENU') {
        const columnId = menuButton.getAttribute('column-id');
        const column = this.dataGrid.columns.find((col) => col.id === columnId);
        const btnId = menuButton.getAttribute('ref-id') ?? '';

        if (column?.filterType) {
          const popupTemplate = this.dataGrid.filters.filterButtonPopupTemplate(btnId, column.filterType.name, column);
          const gridWrapper = this.closest('.ids-data-grid-wrapper');
          if (gridWrapper) {
            const menuExists = gridWrapper.querySelector(`[target='#btn-${btnId}']`);
            if (!menuExists) gridWrapper.insertAdjacentHTML('beforeend', popupTemplate);
          }

          menuButton.configureMenu();
          menuButton.menuEl?.show();
        }
      }
    });
  }

  /**
   * Set header expander state
   */
  setIsHeaderExpanderCollapsed() {
    const all = this.dataGrid?.rows?.filter((r: any) => r?.hasAttribute('aria-expanded')) || [];
    const collapsedRows = all.filter((r: any) => r?.getAttribute('aria-expanded') === 'false');
    if (all.length && all.length === collapsedRows.length) this.isHeaderExpanderCollapsed = true;
  }

  /**
   * Sets the state of the ids-icon.header-expander elements to open/expanded.
   */
  openExpanderIcons() {
    this.isHeaderExpanderCollapsed = false;
    this.expanderIcons?.forEach((iconElement: IdsIcon) => {
      iconElement.icon = `plusminus-folder-open`;
    });
  }

  /**
   * Sets the state of the ids-icon.header-expander elements to closed/collapsed.
   */
  closeExpanderIcons() {
    this.isHeaderExpanderCollapsed = true;
    this.expanderIcons?.forEach((iconElement: IdsIcon) => {
      iconElement.icon = `plusminus-folder-closed`;
    });
  }

  /**
   * Set the header checkbox state
   * @private
   */
  setHeaderCheckbox() {
    if (!this.dataGrid?.rowSelection || this.dataGrid?.rowSelection === 'single' || !this.headerCheckbox) {
      return;
    }

    const selectedCount = this.dataGrid?.selectedRows.length;
    const dataCount = this.dataGrid?.data.length;

    if (selectedCount === 0) {
      this.headerCheckbox.classList.remove('indeterminate');
      this.headerCheckbox.classList.remove('checked');
      this.headerCheckbox.setAttribute('aria-checked', 'false');
      return;
    }

    if (selectedCount === dataCount) {
      this.headerCheckbox.classList.remove('indeterminate');
      this.headerCheckbox.classList.add('checked');
      this.headerCheckbox.setAttribute('aria-checked', 'true');
      return;
    }

    if (selectedCount !== dataCount) {
      this.headerCheckbox.classList.add('indeterminate');
      this.headerCheckbox.setAttribute('aria-checked', 'mixed');
    }
  }

  /**
   * Set the checked status on the column header checkbox
   * @param {number} index Column index
   * @param {boolean} value True for checked, false for unchecked
   */
  setColumnCheckbox(index: number, value: boolean) {
    if (this.columnCheckbox && this.columnCheckbox[index]) {
      const checkbox = this.columnCheckbox[index] as HTMLInputElement;
      checkbox.checked = value;
    }
  }

  /**
   * Sets the disabled attribute of header checkbox
   * @param {boolean} value attribute value to set
   */
  disableHeaderCheckbox(value: boolean) {
    if (!this.headerCheckbox) {
      return;
    }

    this.headerCheckbox.setAttribute('disabled', String(value));
    this.headerCheckbox.parentElement?.setAttribute('disabled', String(value));
  }

  /**
   * Sets the disabled attribute of header checkbox
   * @param {boolean} value attribute value to set
   */
  hideHeaderCheckbox(value: boolean) {
    if (!this.headerCheckbox) {
      return;
    }

    if (value) {
      this.headerCheckbox.setAttribute('hidden', String(value));
    } else {
      this.headerCheckbox.removeAttribute('hidden');
    }
  }

  /**
   * Set the sort column and sort direction on the UI only
   * @param {string} id The column id (or field) to set
   * @param {boolean} ascending Sort ascending (lowest first) or descending (lowest last)
   */
  setSortState(id: string, ascending: boolean) {
    const sortedHeaders = [...this!.querySelectorAll('.is-sortable')]
      .map((sorted) => sorted.closest('.ids-data-grid-header-cell'));
    sortedHeaders.forEach((header) => header?.removeAttribute('aria-sort'));

    const header = this.querySelector(`[column-id="${id}"]`);
    if (header && sortedHeaders.includes(header)) {
      header.setAttribute('aria-sort', ascending ? 'ascending' : 'descending');
    }
  }

  /**
   * Resets sort header sort states
   */
  resetSortStates() {
    this.querySelectorAll('.ids-data-grid-header-cell').forEach((elem) => elem.removeAttribute('aria-sort'));
  }

  /**
   * Set filter row to be shown or hidden
   * @private
   * @returns {object} This API object for chaining
   */
  setFilterRow() {
    const nodes = this?.querySelectorAll('.ids-data-grid-header-cell-filter-wrapper');
    nodes?.forEach((n) => n?.classList?.[this.dataGrid?.filterable ? 'remove' : 'add']('hidden'));
    this.dataGrid?.triggerEvent(this.dataGrid?.filterable ? 'filterrowopened' : 'filterrowclosed', this.dataGrid, {
      detail: { elem: this, filterable: this.dataGrid?.filterable }
    });
    return this;
  }

  /**
   * Return the Template for the header contents
   * @param {IdsDataGrid} datagrid visible columns in the data grid
   * @returns {string} The template
   */
  static template(datagrid: IdsDataGrid): string {
    const html = `<ids-data-grid-header class="ids-data-grid-header${datagrid.hasFrozenColumns ? ' is-frozen' : ''}" role="rowgroup" part="header">
      <ids-data-grid-row class="ids-data-grid-row${datagrid.colSelection ? ' has-col-selection' : ''}" role="row">
        ${datagrid.visibleColumns.map((columnData: any, index: number) => `${this.headerCellTemplate(datagrid, columnData, index)}`).join('')}
      </ids-data-grid-row>
    </ids-data-grid-header>`;
    return this.columnGroupsTemplate(datagrid) + html;
  }

  /**
   * Returns the markup for a header cell.
   * @param {IdsDataGrid} dataGrid visible columns in the data grid
   * @param {IdsDataGridColumn} column The column info
   * @param {number} index The column index
   * @returns {string} The resuling header cell template
   */
  static headerCellTemplate(dataGrid: IdsDataGrid, column: IdsDataGridColumn, index: number) {
    const selectionCheckBoxTemplate = `
      <span class="ids-data-grid-checkbox-container" disabled="${dataGrid.disableHeaderCheckbox}">
        <span
          role="checkbox"
          ${dataGrid.hideHeaderCheckbox ? 'hidden' : ''}
          disabled="${dataGrid.disableHeaderCheckbox}"
          aria-checked="false"
          aria-label="${column.name}"
          class="ids-data-grid-checkbox"
        >
        </span>
      </span>
    `;

    const sortIconSize = dataGrid.rowHeight === 'xxs' ? 'size="small"' : '';
    const sortIndicatorTemplate = `
      <span class="sort-indicator">
        <ids-icon icon="dropdown" ${sortIconSize}></ids-icon>
        <ids-icon icon="dropdown" ${sortIconSize}></ids-icon>
      </span>
    `;

    const expanderTemplate = `<ids-icon class="header-expander" icon="plusminus-folder-closed"></ids-icon>`;
    const resizerTemplate = `<span class="resizer"></span>`;
    const reorderTemplate = `<div class="reorderer"><ids-icon icon="drag" size="medium"></ids-icon></div>`;
    const showFormatterExpander = ['expander', 'tree'].includes(column.formatter?.name ?? '');
    const showHeaderExpander = column?.showHeaderExpander || showFormatterExpander;
    const expander = showHeaderExpander ? expanderTemplate : '';

    const selectionCheckbox = column.id !== 'selectionRadio' && column.id === 'selectionCheckbox';
    let colName = '';
    if (Array.isArray(column.name)) {
      colName = `
        ${escapeHTML(column.name[0].text)}
        <br>
        ${column.name[1].emphasis === 'subtle' ? '<small>' : ''}
        ${escapeHTML(column.name[1].text)}
        ${column.name[1].emphasis === 'subtle' ? '</small>' : ''}
      `;
    } else { colName = escapeHTML(column.name); }

    const headerName = column.id !== 'selectionRadio' && column.id !== 'selectionCheckbox' && colName ? colName : '';
    const headerLabel = `${dataGrid?.colSelection && column.id !== 'selectionRadio' && column.id !== 'selectionCheckbox'
      ? `<div class="ids-checkbox">
        <label>
          <input type="checkbox"/>
          <span class="checkmark"></span>
          <span class="ids-text label-text">${headerName}</span>
        </label>
      </div>` : headerName}`;

    const headerContentTemplate = `
      ${selectionCheckbox ? selectionCheckBoxTemplate : ''}
      ${headerLabel}
    `.trim();

    let cssClasses = 'ids-data-grid-header-cell-content';
    cssClasses += column.sortable ? ' is-sortable' : '';
    cssClasses += selectionCheckbox ? ' has-selectioncheckbox vertical-align-center' : '';
    cssClasses += column.headerIcon ? ' has-headericon' : '';
    cssClasses += column.reorderable ? ' is-reorderable' : '';
    cssClasses += column?.showHeaderExpander ? ' column-header-expander' : '';

    // Content row cell template
    const headerContentWrapperTemplate = `<span class="${cssClasses}">
        ${expander}
        ${column.sortable && (column.headerAlign === 'right' || (!column.headerAlign && column.align === 'right')) ? sortIndicatorTemplate : ''}
        <span class="ids-data-grid-header-text">
          ${headerContentTemplate}
        </span>
        ${this.headerIconTemplate(column)}
        ${column.sortable && (column.headerAlign !== 'right' && (!column.headerAlign ? column.align !== 'right' : true)) ? sortIndicatorTemplate : ''}
      </span>${column.resizable ? resizerTemplate : ''}${column.reorderable ? reorderTemplate : ''}`;

    // Filter row cell template
    const headerFilterWrapperTemplate = dataGrid?.filters?.filterTemplate(column) || '';
    let align = column.align ? ` align-${column.align}` : '';
    if (column.headerAlign) {
      align = ` align-${column.headerAlign}`;
    }

    // Frozen Classes
    const lastFrozen = dataGrid?.leftFrozenColumns.length;
    let frozen = column?.frozen ? ` frozen frozen-${column?.frozen}${index + 1 === lastFrozen ? ' frozen-last' : ''}` : '';

    // Check for far left frozen cells just in case
    const lastFrozenData = dataGrid.visibleColumns.filter((obj) => obj.frozen === 'left').at(-1);
    const lastFrozenIndex = dataGrid.visibleColumns.findIndex((obj) => obj.id === lastFrozenData?.id);
    if (frozen === '' && index < lastFrozenIndex) {
      frozen = ` frozen frozen-${lastFrozenData?.frozen}`;
      column.frozen = lastFrozenData?.frozen;
    }

    if (index === lastFrozenIndex && frozen !== '' && frozen.indexOf('last') < 0) frozen += ` frozen-last`;

    const isUppercase = (): boolean => {
      if (typeof column?.uppercase === 'function') return column.uppercase('header-cell', column, index);
      return (column?.uppercase === 'true' || column?.uppercase === true);
    };

    let headerCssClasses = isUppercase() ? ' is-uppercase' : '';
    headerCssClasses += (column?.showHeaderExpander || column.sortable || column.reorderable) ? ' is-actionable' : '';

    // Header cell template
    const html = `
      <span
        class="ids-data-grid-header-cell${headerCssClasses}${align}${frozen}"
        ${column.reorderable ? 'draggable="true"' : ''}
        part="header-cell"
        aria-colindex="${index + 1}"
        column-id="${column.id}"
        role="columnheader">
        ${headerContentWrapperTemplate}
        ${headerFilterWrapperTemplate}
      </span>
    `;

    return html;
  }

  /**
   * Returns the markup for a header icon.
   * @param {IdsDataGridColumn | IdsDataGridColumnGroup} column The column info
   * @returns {string} The resuling header icon template
   */
  static headerIconTemplate(column: IdsDataGridColumn | IdsDataGridColumnGroup): string {
    const headerIcon = typeof column?.headerIcon === 'string' ? column.headerIcon : '';
    if (headerIcon === '') return '';

    const headerIconTooltip = column.headerIconTooltip || headerIcon;
    return `
      <span class="ids-data-grid-header-icon" data-headericontooltip="${headerIconTooltip}">
        <ids-icon icon="${headerIcon}" size="medium"></ids-icon>
      </span>`;
  }

  /**
   * Returns the markup for the grouped header cells.
   * @param {IdsDataGrid} dataGrid The datagrid reference
   * @returns {string} The resuling header cell template
   */
  static columnGroupsTemplate(dataGrid: IdsDataGrid) : string {
    if (!dataGrid?.columnGroups) {
      return '';
    }
    let columnGroupHtml = `<ids-data-grid-header class="ids-data-grid-header column-groups" part="header" role="rowgroup">
    <div role="row" class="ids-data-grid-row ids-data-grid-column-groups">`;

    dataGrid?.columnGroups.forEach((columnGroup: IdsDataGridColumnGroup) => {
      const align = columnGroup.align ? ` align-${columnGroup.align}` : '';

      // Header cell template
      let cssClasses = 'ids-data-grid-header-cell-content';
      cssClasses += columnGroup.headerIcon ? ' has-headericon' : '';

      const html = `<span class="ids-data-grid-header-cell${align}" part="header-cell" column-group-id="${columnGroup.id || 'id'}" role="columnheader">
        <span class="${cssClasses}">
          <span class="ids-data-grid-header-text">
            ${columnGroup.name || ''}
          </span>
          ${this.headerIconTemplate(columnGroup)}
        </span>
      </span>`;
      columnGroupHtml += html;
    });

    columnGroupHtml += '</div></ids-data-grid-header>';

    return columnGroupHtml;
  }
}
